<template>
  <v-card :title="t('resize_park')" :disabled="disabled">
    <v-card-text>
      <v-row>
        <v-col cols="6" class="align-self-center">
          <v-sheet outlined color="red" rounded class="pa-1">
            <v-card>
              <v-card-title>
                <p style="color: #ff0000; text-align: center">
                  {{ t('use_with_caution') }}
                </p>
              </v-card-title>
              <v-card-text>
                <v-text-field
                  :label="t('number_of_cs')"
                  type="number"
                  hide-details
                  v-model="nCs"
                />
              </v-card-text>
              <v-card-actions class="justify-end">
                <v-btn border @click="submitDry">{{ t('execute') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-sheet>
        </v-col>
        <v-col>
          <b>{{ t('warning_1') }}</b><br/>
          {{ t('warning_2') }}<br/>
          <v-divider thickness="2" class="mt-4 mb-4"/>
          <b>{{ t('warning_fr_1') }}</b><br/>
          {{ t('warning_fr_2') }}
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
  <v-overlay v-model="showConfirmOverlay" class="d-flex justify-center align-center">
    <v-card :title="t('confirm_changes')">
      <v-card-text>
        {{ confirmBody }}
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn @click="showConfirmOverlay = false">{{ t('cancel') }}</v-btn>
        <v-btn @click="submitReal">{{ t('confirm') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-overlay>
</template>

<script setup>
import { ref, toRefs, watch } from 'vue';
import { resizePark } from "../../remote/park.js";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });
// General State
const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const props = defineProps(['originalPark', 'disabled']);
const { originalPark, disabled } = toRefs(props);
const emit = defineEmits(['updated']);

// State
const nCs = ref(0);
const showConfirmOverlay = ref(false);
const confirmBody = ref(null);

watch(originalPark, (newValue) => {
  nCs.value = newValue['chargingstations']?.length || 0;
});

async function submitDry() {
  loadingOverlay.value = true;
  confirmBody.value = await resizePark(useAlerts, originalPark.value.id, nCs.value, true);
  loadingOverlay.value = false;
  showConfirmOverlay.value = true;
}

async function submitReal() {
  showConfirmOverlay.value = false;
  loadingOverlay.value = true;
  await resizePark(useAlerts, originalPark.value.id, nCs.value, false);
  loadingOverlay.value = false;
  useAlerts.value.successAlert("Park resized succcessfully!", "We have reloaded the page for you!");
  emit('updated');
}

</script>

<i18n>
{
  "en": {
    "resize_park": "Resize Park",
    "use_with_caution": "USE WITH CAUTION",
    "number_of_cs": "Number of CS",
    "execute": "Execute",
    "warning_1": "Any information lost with the destruction of some chargepoints cannot be recovered even by the software team.",
    "warning_2": "If you have to completely cancel a park, contact them through the usual channels. If you have any doubts, ask before deleting something you cannot restore.",
    "warning_fr_1": "Toute information perdue avec la destruction de certaines bornes de recharge ne peut être récupérée même par l'équipe software.",
    "warning_fr_2": "Si vous devez supprimer complètement un parc, contactez-nous via les canaux habituels. Si vous avez des doutes, demandez avant de supprimer quelque chose que vous ne pouvez pas restaurer.",
    "confirm_changes": "Do you confirm this changes?",
    "cancel": "Cancel",
    "confirm": "Confirm"
  },
  "it": {
    "resize_park": "Ridimensiona Parco",
    "use_with_caution": "USARE CON CAUTELA",
    "number_of_cs": "Numero di CS",
    "execute": "Esegui",
    "warning_1": "Qualsiasi informazione persa con la distruzione di alcuni punti di ricarica non può essere recuperata nemmeno dal team software.",
    "warning_2": "Se devi cancellare completamente un parco, contattali tramite i canali consueti. Se hai dubbi, chiedi prima di cancellare qualcosa che non puoi ripristinare.",
    "warning_fr_1": "Toute information perdue avec la destruction de certaines bornes de recharge ne peut être récupérée même par l'équipe software.",
    "warning_fr_2": "Si vous devez supprimer complètement un parc, contactez-nous via les canaux habituels. Si vous avez des doutes, demandez avant de supprimer quelque chose que vous ne pouvez pas restaurer.",
    "confirm_changes": "Confermi queste modifiche?",
    "cancel": "Annulla",
    "confirm": "Conferma"
  },
  "fr": {
    "resize_park": "Redimensionner le Parc",
    "use_with_caution": "UTILISER AVEC PRUDENCE",
    "number_of_cs": "Nombre de CS",
    "execute": "Exécuter",
    "warning_1": "Toute information perdue avec la destruction de certaines bornes de recharge ne peut être récupérée même par l'équipe software.",
    "warning_2": "Si vous devez supprimer complètement un parc, contactez-nous via les canaux habituels. Si vous avez des doutes, demandez avant de supprimer quelque chose que vous ne pouvez pas restaurer.",
    "warning_fr_1": "Toute information perdue avec la destruction de certaines bornes de recharge ne peut être récupérée même par l'équipe software.",
    "warning_fr_2": "Si vous devez supprimer complètement un parc, contactez-nous via les canaux habituels. Si vous avez des doutes, demandez avant de supprimer quelque chose que vous ne pouvez pas restaurer.",
    "confirm_changes": "Confirmez-vous ces changements?",
    "cancel": "Annuler",
    "confirm": "Confirmer"
  }
}
</i18n>

<style scoped>

</style>
